<template>
  <div class="sf-page">
    <top-header v-bind:title="$t('customers')">
      <v-menu offset-y
              left
              slot="menu">
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="primary"
                 outlined
                 v-bind="attrs"
                 v-on="on">
            {{ $t('actions') }}
            <v-icon right>ti-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-on:click="openSendDialog(null)">
            <v-list-item-icon class="mr-3"><v-icon>ti-mail-fast</v-icon></v-list-item-icon>
            <v-list-item-title>{{ $t('sendEmail') }}</v-list-item-title>
          </v-list-item>
          <v-list-item v-on:click="exportCustomers">
            <v-list-item-icon class="mr-3"><v-icon>ti-file-spreadsheet</v-icon></v-list-item-icon>
            <v-list-item-title>{{ $t('export') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </top-header>
    <email-test-mode-banner />
    <v-progress-linear color="accent"
                       indeterminate
                       v-bind:active="loading" />
    <send-email-dialog audience="Customer"
                       v-bind:recipient-ids="sendRecipientIds"
                       v-bind:recipient-label="sendRecipientLabel"
                       v-model="sendDialog"
                       v-on:created="onJobCreated" />
    <email-job-progress ref="jobProgress" />
    <div class="sf-container">
      <customer-list v-bind:customers="customers"
                     v-on:send-email-message="sendIndividualEmailMessage" />
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'

  import CustomerList from '@/components/administration/CustomerList'
  import EmailJobProgress from '@/components/administration/EmailJobProgress'
  import EmailTestModeBanner from '@/components/administration/EmailTestModeBanner'
  import SendEmailDialog from '@/components/administration/SendEmailDialog'
  import TopHeader from '@/components/common/TopHeader'
  import { guests } from '@/utils/customer-guests'

  export default {
    components: {
      CustomerList,
      EmailJobProgress,
      EmailTestModeBanner,
      SendEmailDialog,
      TopHeader
    },
    computed: {
      ...mapGetters([ 'customers', 'customEmailTemplates' ])
    },
    async created() {
      this.loading = true
      await this.$store.dispatch('loadCustomEmailTemplates')
      this.$store.dispatch('loadEmailDesignerData')
      await this.$store.dispatch('loadCustomers')
      
      this.loading = false
    },
    data: () => ({
      loading: false,
      sendDialog: false,
      sendRecipientIds: null,
      sendRecipientLabel: null
    }),
    methods: {
      ...mapActions([
        'deleteCustomer'
      ]),
      openSendDialog(ids, label) {
        this.sendRecipientIds = ids
        this.sendRecipientLabel = label || null
        this.sendDialog = true
      },
      onJobCreated(job) {
        this.$refs.jobProgress.start(job)
      },
      exportCustomers() {
        if (!this.customers || this.customers.length === 0) {
          return
        }

        const guestSourceFields = [
          'firstGuestGivenName',
          'firstGuestFamilyName',
          'firstGuestEmail',
          'secondGuestGivenName',
          'secondGuestFamilyName',
          'secondGuestEmail',
          'guestGivenName3',
          'guestFamilyName3',
          'guestEmail3',
          'guestGivenName4',
          'guestFamilyName4',
          'guestEmail4',
          'guestGivenName5',
          'guestFamilyName5',
          'guestEmail5',
          'guestGivenName6',
          'guestFamilyName6',
          'guestEmail6',
          'guestGivenName7',
          'guestFamilyName7',
          'guestEmail7',
          'guestGivenName8',
          'guestFamilyName8',
          'guestEmail8',
          'guestGivenName9',
          'guestFamilyName9',
          'guestEmail9',
          'guestGivenName10',
          'guestFamilyName10',
          'guestEmail10'
        ]

        for (let i = 1; i <= 10; i++) {
          guestSourceFields.push(`guestInvitationCode${i}`, `guestWasAttending${i}`)
        }

        // Je Gastzeile: Name, E-Mail, eigener Einladungscode und Anwesenheit (Namensschilder)
        const derivedGuestFields = [ 'guestGivenName', 'guestFamilyName', 'guestEmail', 'guestInvitationCode', 'guestWasAttending' ]

        const baseCustomerKeys = Object.keys(this.customers[0])
          .filter(key => !guestSourceFields.includes(key))
          .filter(key => !derivedGuestFields.includes(key))

        const headers = [ ...baseCustomerKeys, ...derivedGuestFields ]

        const csvEscape = (value) => {
          if (value === null || value === undefined) {
            return '""'
          }
          const text = String(value)
          return '"' + text.replace(/"/g, '""') + '"'
        }

        const makeEmptyRow = () => {
          const row = {}
          for (const header of headers) {
            row[header] = ''
          }
          return row
        }

        const extractGuests = (customer) => guests(customer).map(guest => ({
          guestGivenName: guest.givenName || '',
          guestFamilyName: guest.familyName || '',
          guestEmail: guest.email || '',
          guestInvitationCode: guest.invitationCode || '',
          guestWasAttending: guest.wasAttending
        }))

        let csv = ''
        csv += headers.map(csvEscape).join(',') + '\r\n'

        for (const customer of this.customers) {
          const customerRow = makeEmptyRow()

          for (const key of baseCustomerKeys) {
            customerRow[key] = customer[key] ?? ''
          }
          for (const field of derivedGuestFields) {
            customerRow[field] = ''
          }

          csv += headers.map(h => csvEscape(customerRow[h])).join(',') + '\r\n'

          const guests = extractGuests(customer)
          for (const guest of guests) {
            const guestRow = makeEmptyRow()
            if ('id' in guestRow) {
              guestRow.id = customer.id ?? ''
            }
            for (const field of derivedGuestFields) {
              guestRow[field] = guest[field]
            }

            csv += headers.map(h => csvEscape(guestRow[h])).join(',') + '\r\n'
          }
        }

        const blob = new Blob([ '\ufeff' + csv ], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const element = document.createElement('a')
        element.setAttribute('href', url)
        element.setAttribute('download', 'export.csv')
        element.style.display = 'none'

        document.body.appendChild(element)
        element.click()
        document.body.removeChild(element)
        URL.revokeObjectURL(url)
      },
      sendIndividualEmailMessage({ id }) {
        const customer = this.customers.find(c => c.id === id)

        this.openSendDialog([ id ], customer ? `${customer.companyName} (${customer.id})` : id)
      }

    },
    name: 'Customers'
  }
</script>

<i18n>
  {
    "de": {
      "export": "Exportieren…",
      "sendEmail": "E-Mail senden…",
      "customers": "Kunden",
      "import": "Importieren…",
      "inviteAll": "Alle einladen",
      "remindAll": "Alle erinnern",
      "sendStartInfo": "Startinformation an Alle senden",
      "sendEndReminder": "Enderinnerung an Alle senden",
      "sendEndMessage": "Endbenachrichtigung an Alle senden",
      "sendEvaluation": "Zufriedenheits-Umfrage senden",
      "actions": "Aktionen"
    },
    "en": {
      "export": "Export…",
      "sendEmail": "Send email…",
      "customers": "Customers",
      "import": "Import…",
      "inviteAll": "Invite all",
      "remindAll": "Remind all",
      "sendStartInfo": "Send start info",
      "sendEndReminder": "Send end reminder",
      "sendEndMessage": "Send end message",
      "sendEvaluation": "Send evaluation",
      "actions": "Actions"
    }
  }
</i18n>